import React, { useState, useEffect } from 'react';
import { Table, Card, Row, Col, Select, DatePicker, Button, Input, Modal, Descriptions, Switch, Dropdown, Menu, Tag, Space } from 'antd';
import { SearchOutlined, ReloadOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import useRequest from '../hooks/useRequest';
import apiPath from '../constants/apiPath';
import { Severty, ShowToast } from '../helper/toast';
import '../assets/styles/transactions.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

const Transactions = () => {
  const { request } = useRequest();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    vendor: '',
    client: '',
    status: '',
    start_date: '',
    end_date: '',
  });
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const statusColors = {
    'on_hold': 'orange',
    'approved': 'blue',
    'paid': 'green',
    'failed': 'red'
  };

  const columns = [
    {
      title: 'Transaction ID',
      dataIndex: 'transaction_id',
      key: 'transaction_id',
      width: 150,
    },
    {
      title: 'Vendor Name',
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      width: 150,
    },
    {
      title: 'Client Name',
      dataIndex: 'client_name',
      key: 'client_name',
      width: 150,
    },
    {
      title: 'Order ID',
      dataIndex: 'order_id',
      key: 'order_id',
      width: 180,
    },
    {
      title: 'Service',
      dataIndex: 'service_name',
      key: 'service_name',
      width: 150,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => amount ? `$${amount}` : '$0',
      width: 100,
      align: 'right',
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (created_at) => {
        if (!created_at) return 'N/A';
        try {
          const date = new Date(created_at);
          if (isNaN(date)) return 'Invalid Date';
          return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          });
        } catch (error) {
          return 'Invalid Date';
        }
      },
      width: 180,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={statusColors[status] || 'default'}>
          {status?.replace('_', ' ').toUpperCase()}
        </Tag>
      ),
      width: 120,
    },
    {
      title: 'Invoice',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      width: 150,
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 80,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewTransaction(record)}
        >
          View
        </Button>
      ),
    },
  ];

  const fetchTransactions = async (page = 1, pageSize = 10) => {
    setLoading(true);
    
    const params = new URLSearchParams({
      page,
      page_size: pageSize,
      ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
    });

    await request({
      url: `${apiPath.getTransaction}?${params}`,
      method: 'GET',
      onSuccess: (response) => {
        if (response.status) {
          setTransactions(response.data || []);
          setPagination({
            current: response.pagination?.current_page || page,
            pageSize: response.pagination?.page_size || pageSize,
            total: response.pagination?.total_count || 0,
          });
        }
        setLoading(false);
      },
      onError: () => {
        setLoading(false);
      }
    });
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const handleTableChange = (paginationInfo) => {
    fetchTransactions(paginationInfo.current, paginationInfo.pageSize);
  };

  const handleSearch = () => {
    fetchTransactions(1, pagination.pageSize);
  };

  const handleReset = () => {
    setFilters({
      vendor: '',
      client: '',
      status: '',
      start_date: '',
      end_date: '',
    });
    fetchTransactions(1, pagination.pageSize);
  };

  const handleDateChange = (dates) => {
    if (dates) {
      setFilters({
        ...filters,
        start_date: dates[0].format('YYYY-MM-DD'),
        end_date: dates[1].format('YYYY-MM-DD'),
      });
    } else {
      setFilters({
        ...filters,
        start_date: '',
        end_date: '',
      });
    }
  };

  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setViewModalVisible(true);
  };

  return (
    <div className="transactions">
      <Card title="Vendor Transactions" style={{ margin: '20px' }}>
        {/* Search and Filter Section */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]} align="bottom">
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="Search vendor by name or email"
                value={filters.vendor}
                onChange={(e) => setFilters({ ...filters, vendor: e.target.value })}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="Search by order ID or service"
                value={filters.client}
                onChange={(e) => setFilters({ ...filters, client: e.target.value })}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                placeholder="Select Status"
                value={filters.status || undefined}
                onChange={(value) => setFilters({ ...filters, status: value })}
                style={{ width: '100%' }}
                allowClear
              >
                <Option value="on_hold">On Hold</Option>
                <Option value="approved">Approved</Option>
                <Option value="paid">Paid</Option>
                <Option value="failed">Failed</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <RangePicker
                onChange={handleDateChange}
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                  Search
                </Button>
                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                  Reset
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        <Table
          columns={columns}
          dataSource={transactions}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} items`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
          rowKey="id"
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* View Transaction Modal */}
      <Modal
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>
        ]}
        className='tab_modal'
        width={700}
      >
        <div className='modal_header'>
          Transaction Details
        </div>
        {selectedTransaction && (
          <Descriptions bordered column={1} size="middle">
            <Descriptions.Item label="Transaction ID">
              {selectedTransaction.transaction_id}
            </Descriptions.Item>
            <Descriptions.Item label="Order ID">
              {selectedTransaction.order_id}
            </Descriptions.Item>
            <Descriptions.Item label="Invoice Number">
              {selectedTransaction.invoice_number}
            </Descriptions.Item>
            <Descriptions.Item label="Vendor Name">
              {selectedTransaction.vendor_name}
            </Descriptions.Item>
            <Descriptions.Item label="Vendor Email">
              {selectedTransaction.vendor_email}
            </Descriptions.Item>
            <Descriptions.Item label="Client Name">
              {selectedTransaction.client_name}
            </Descriptions.Item>
            <Descriptions.Item label="Client Email">
              {selectedTransaction.client_email}
            </Descriptions.Item>
            <Descriptions.Item label="Service Name">
              {selectedTransaction.service_name}
            </Descriptions.Item>
            <Descriptions.Item label="Amount">
              <strong>${selectedTransaction.amount}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={statusColors[selectedTransaction.status] || 'default'}>
                {selectedTransaction.status?.replace('_', ' ').toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {selectedTransaction.created_at ? new Date(selectedTransaction.created_at).toLocaleString() : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Updated At">
              {selectedTransaction.updated_at ? new Date(selectedTransaction.updated_at).toLocaleString() : 'N/A'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default Transactions;